import { securedAxiosInstance } from "../axios";
import { toSnakeCase } from "@estaly/ui";

class ClaimsService {
  // === Claims core ===

  fetch(friendlyId, includes = []) {
    return securedAxiosInstance.get(`/claims/${friendlyId}`, {
      params: { includes },
    });
  }

  fetchIndex(params) {
    return securedAxiosInstance.get("/claims", {
      params: toSnakeCase({
        ...params,
        includes: ["store", "customer", "plan_sale", "claim_product", "fraud_suspicion_flagged_by", "assignee"],
      }),
    });
  }

  exportClaimData(claimId) {
    return securedAxiosInstance.post(`/claims/${claimId}/export`);
  }

  bulkExportClaims(params) {
    return securedAxiosInstance.post("/claims/bulk_export", toSnakeCase(params));
  }

  approveClaim(claimId, params) {
    return securedAxiosInstance.patch(`/claims/${claimId}/approve`, toSnakeCase(params));
  }

  refuseClaim(claimId, params) {
    return securedAxiosInstance.patch(`/claims/${claimId}/refuse`, toSnakeCase(params));
  }

  updateClaim(claimId, params) {
    return securedAxiosInstance.put(`/claims/${claimId}`, toSnakeCase(params));
  }

  updateEstalyManagerNotes(id, params) {
    return securedAxiosInstance.put(`/claim_historical_datas/${id}`, toSnakeCase(params));
  }

  specifyHandlingType(claimId, params) {
    return securedAxiosInstance.patch(`/claims/${claimId}/specify_handling_type`, toSnakeCase(params));
  }

  uploadQuotation(claimId, formData) {
    return securedAxiosInstance.patch(`/claims/${claimId}/upload_quotation`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  declareIrreparable(claimId, formData) {
    return securedAxiosInstance.patch(`/claims/${claimId}/declare_irreparable`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  approveQuotation(claimId) {
    return securedAxiosInstance.patch(`/claims/${claimId}/approve_quotation`);
  }

  refuseQuotation(claimId, params) {
    return securedAxiosInstance.patch(`/claims/${claimId}/refuse_quotation`, toSnakeCase(params));
  }

  generateVoucher(claimId) {
    return securedAxiosInstance.patch(`/claims/${claimId}/create_voucher`);
  }

  resetHandlingType(claimId) {
    return securedAxiosInstance.patch(`/claims/${claimId}/reset_handling_type`);
  }

  uploadInvoice(claimId, formData) {
    return securedAxiosInstance.patch(`/claims/${claimId}/upload_invoice`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  approveInvoice(claimId, params) {
    return securedAxiosInstance.patch(`/claims/${claimId}/approve_invoice`, toSnakeCase(params));
  }

  refuseInvoice(claimId, params) {
    return securedAxiosInstance.patch(`/claims/${claimId}/refuse_invoice`, toSnakeCase(params));
  }

  resolveClaim(claimId, params) {
    return securedAxiosInstance.patch(`/claims/${claimId}/resolve`, toSnakeCase(params));
  }

  cancelClaim(claimId, params) {
    return securedAxiosInstance.patch(`/claims/${claimId}/cancel`, toSnakeCase(params));
  }

  requestAdditionalInfo(claimId, params) {
    return securedAxiosInstance.patch(`/claims/${claimId}/request_additional_info`, toSnakeCase(params));
  }

  reopen(claimId) {
    return securedAxiosInstance.patch(`/claims/${claimId}/reopen`);
  }

  updateTicketStatus(claimId, params) {
    return securedAxiosInstance.patch(`/claims/${claimId}/update_ticket_status`, toSnakeCase(params));
  }

  fetchInboxCounts() {
    return securedAxiosInstance.get("/claims/inbox_counts");
  }

  assign(friendlyId, assigneeId) {
    return securedAxiosInstance.patch(`/claims/${friendlyId}/assignment`, { assignee_id: assigneeId });
  }

  updateFraudSuspicion(claimId, params) {
    return securedAxiosInstance.patch(`/claims/${claimId}/update_fraud_suspicion`, toSnakeCase(params));
  }

  // === Claim Deductibles ===

  fetchDeductibleInvoiceUrl(id) {
    return securedAxiosInstance.get(`/claims/claim_deductibles/${id}/invoice_url`);
  }

  // === Final Settlements ===

  fetchFinalSettlementInvoiceUrl(id) {
    return securedAxiosInstance.get(`/claims/final_settlements/${id}/invoice_url`);
  }

  // === Unfixeable Claim Products ===

  fetchUnfixeableClaimProducts(params) {
    return securedAxiosInstance.get("/claims/unfixeable_claim_products", { params: toSnakeCase(params) });
  }

  fetchUnfixeableClaimProduct(id, params) {
    return securedAxiosInstance.get(`/claims/unfixeable_claim_products/${id}`, { params: toSnakeCase(params) });
  }

  updateUnfixeableClaimProduct(id, data) {
    return securedAxiosInstance.put(`/claims/unfixeable_claim_products/${id}`, toSnakeCase(data));
  }

  // === Claim Declarations ===

  fetchDeclarations(params) {
    return securedAxiosInstance.get("/claims_declaration", { params: toSnakeCase(params) });
  }

  // === Claim Quotations ===

  fetchQuotations(params) {
    return securedAxiosInstance.get("/claims/quotations", { params: toSnakeCase(params) });
  }

  updateQuotationCostItems(quotationId, costItems) {
    return securedAxiosInstance.patch(`/claims/quotations/${quotationId}`, { cost_items: costItems });
  }

  // === Claim Invoices ===

  fetchClaimInvoices(params) {
    return securedAxiosInstance.get("/claims/invoices", { params: toSnakeCase(params) });
  }

  updateInvoiceCostItems(invoiceId, costItems) {
    return securedAxiosInstance.patch(`/claims/invoices/${invoiceId}`, { cost_items: costItems });
  }

  getClaimInvoicesTransfers(claimInvoiceIds) {
    return securedAxiosInstance.get("/claims/invoices/transfers", {
      params: toSnakeCase({ claimInvoiceIds }),
    });
  }

  markClaimInvoicesAsPaid(claimInvoiceIds) {
    return securedAxiosInstance.put("/claims/invoices/paid", toSnakeCase({ claimInvoiceIds }));
  }

  // === Cost Items Extraction ===

  extractCostItems(claimId, file) {
    const formData = new FormData();
    formData.append("file", file);
    return securedAxiosInstance.post(`/claims/${claimId}/extract_cost_items`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  extractReplacementProduct(claimId, file) {
    const formData = new FormData();
    formData.append("file", file);
    return securedAxiosInstance.post(`/claims/${claimId}/extract_replacement_product`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  analyzeDocumentForApproval(claimId, file, documentKind) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("document_kind", documentKind);
    return securedAxiosInstance.post(`/claims/${claimId}/analyze_document_for_approval`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  // === Payment ===

  getPaymentUrl(claimId) {
    return securedAxiosInstance.get(`/claims/${claimId}/payment_link`);
  }
}

export default new ClaimsService();
